import React, { useCallback, useMemo, useRef, useState, useEffect } from 'react';
import type { AnimationItem } from 'lottie-web';
import LottiePlayer from './LottiePlayer';
import uiSchema from './Basic-Interface.json';
import type { FormState, FormValue, UiField } from '../types/forms';
import { buildStateKey } from '../types/forms';
import HamburgerAnimation from '../assets/Lottie/Hamburger.json';
import ToggleAnimation from '../assets/Lottie/Toggle.json';
import './SideMenu.css';

import { type DirectWriteSupport } from '../utils/directWrite';

const typedUiSchema = uiSchema as UiField[];

export const SIDE_MENU_EXPANDED_WIDTH = 340;
export const SIDE_MENU_COLLAPSED_WIDTH = 72;
const HAMBURGER_END_FRAME = 30;
const TOGGLE_END_FRAME = 70;
const DIRECT_WRITE_KEY = buildStateKey('upload_settings', 'Direct Write (in-place)');

function isFieldVisible(field: UiField, formState: FormState) {
    if (!field.dependencies || field.dependencies.length === 0) {
        return true;
    }
    return field.dependencies.every((dep: string) => {
        const depKey = buildStateKey(field.section, dep);
        return !!formState[depKey];
    });
}

function isStaticField(field: UiField) {
    const STATIC_BATCH_KEYS = [
        "input_folder", "analysis_mode", "output_mode", "ai_settings_abilita_riconoscimento_ai",
        "ai_settings_riconoscimento_contestuale", "ai_settings_soglia_affidabilita",
        "ai_settings_soglia_affidabilita_contestuale", "gps_metadata_sovrascrivere",
        "date_metadata_sovrascrivere", "date_metadata_data_manuale", "detection_mode_ricerca_automatica",
        "geocoding_result_localita", "detection_mode_modalita_applicazione", "ai_hint_mode_modalita",
        "ai_hint_mode_hint", "ai_hint_mode_applicazione", "geocoder_ai_conflict_opzione",
        "geocoder_ai_conflict_applica", "rename", "logging", "upload_settings_rateyourconnection"
        , DIRECT_WRITE_KEY
    ];
    const canonicalMap: Record<string, string> = {
        "input_folder_foldertoanalyze": "input_folder",
        "output_mode_createnewfilesoroverwriteexistingones?": "output_mode",
        "ai_settings_enableairecognitiononimages": "ai_settings_abilita_riconoscimento_ai",
        "ai_settings_contextualrecognition": "ai_settings_riconoscimento_contestuale",
        "ai_settings_minimumaireliabilitythreshold%": "ai_settings_soglia_affidabilita",
        "ai_settings_minimumaireliabilitythreshold%forcontextualanalysis": "ai_settings_soglia_affidabilita_contestuale",
        "gps_metadata_overwriteexistinggpsmetadata?": "gps_metadata_sovrascrivere",
        "date_metadata_overwriteexistingdatemetadata?": "date_metadata_sovrascrivere",
        "date_metadata_manuallyenterglobaldate": "date_metadata_data_manuale",
        "detection_mode_automaticlocationsearch": "detection_mode_ricerca_automatica",
        "geocoding_result_foundthefollowinglocationsfromfile/foldernames:": "geocoding_result_localita",
        "detection_mode_choiceapplicationmode?": "detection_mode_modalita_applicazione",
        "ai_mode_doyouwanttouseaiimagerecognitiontorefinalgpscoordinatesofthephoto?": "ai_hint_mode_modalita",
        "ai_hint_mode_selectthecity/geographiccontexttouseforcontextualanalysisfromthefollowingoptions:": "ai_hint_mode_hint",
        "ai_hint_mode_howdoyouwanttoapplythischoiceforsubsequentfileanalysis?": "ai_hint_mode_applicazione",
        "geocoder-ai_conflict_selectthecity/geographiccontexttouseforcontextualanalysisfromthefollowingoptions:": "geocoder_ai_conflict_opzione",
        "geocoder-ai_conflict_doyouwanttoapplygeocoder/manuallocationandcoordinatestoallsubsequentfiles?": "geocoder_ai_conflict_manuale",
        "geocoder-ai_conflict_doyouwanttoapplyimagerecognitionlocationandcoordinatestoallsubsequentfiles?": "geocoder_ai_conflict_riconoscimento",
        "rename_automaticfilerenamingaccordingtocityanddate": "rename",
        "logging_savedetaillogs": "logging",
        "gps_metadata_overwriteexistinggpsmetadata": "gps_metadata_sovrascrivere",
        "date_metadata_overwriteexistingdatemetadata": "date_metadata_sovrascrivere",
        "ai_settings_aianalysismode": "analysis_mode",
        "upload_settings_rateyourconnection": "upload_settings_rateyourconnection",
    };
    const k = buildStateKey(field.section, field.label);
    const key = canonicalMap[k] || k;
    if (field.type && field.type.toLowerCase().includes('dynamic')) return false;
    if (field.section === "AI mode" || field.section === "AI hint mode") return false;
    return STATIC_BATCH_KEYS.includes(key);
}

const AI_TOGGLE_KEY = buildStateKey('ai_settings', 'Enable AI recognition on images');
const CONTEXTUAL_KEY = buildStateKey('ai_settings', 'Contextual recognition');
const RATE_CONNECTION_KEY = buildStateKey('upload_settings', 'Rate your connection');
const DIRECT_WRITE_WARNING = "This option is not supported by your current device/browser";
const DIRECT_WRITE_DISABLED_MESSAGE = "Requires a compatible browser/device. Your current device does not support this functionality. You can still download your geotagged files.";

const ChevronIcon: React.FC<{ expanded?: boolean; size?: number }> = ({ expanded, size = 16 }) => (
    <svg
        className={`chevron-icon ${expanded ? 'chevron-icon--expanded' : ''}`}
        width={size}
        height={size}
        viewBox="0 0 24 24"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
    >
        <path
            d="M6 9l6 6 6-6"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinecap="round"
            strokeLinejoin="round"
        />
    </svg>
);

export const AnimatedToggle: React.FC<{ checked: boolean; onChange: (next: boolean) => void; onBeforeChange?: (next: boolean) => boolean; disabled?: boolean }> = ({ checked, onChange, onBeforeChange, disabled }) => {
    const animationRef = useRef<AnimationItem | null>(null);
    const [ready, setReady] = useState(false);
    const prevValueRef = useRef<boolean | null>(null);
    const frameCountRef = useRef<number>(60);
    const completionHandlerRef = useRef<(() => void) | null>(null);
    const skipNextSyncRef = useRef(false);

    const handleRef = useCallback((instance: AnimationItem) => {
        animationRef.current = instance;
        const lastFrame = Math.max(1, Math.round((instance.totalFrames ?? (TOGGLE_END_FRAME + 1)) - 1));
        frameCountRef.current = lastFrame;
        setReady(true);
    }, []);

    useEffect(() => () => {
        animationRef.current = null;
        completionHandlerRef.current = null;
    }, []);

    const animateToState = useCallback(
        (target: boolean, instant = false) => {
            if (!ready || !animationRef.current) return;
            const instance = animationRef.current;
            const lastFrame = frameCountRef.current || TOGGLE_END_FRAME;

            if (completionHandlerRef.current) {
                instance.removeEventListener?.('complete', completionHandlerRef.current);
                completionHandlerRef.current = null;
            }

            const stopAt = (frame: number) => {
                instance.goToAndStop(frame, true);
                prevValueRef.current = target;
            };

            if (instant || prevValueRef.current === null || prevValueRef.current === target) {
                stopAt(target ? lastFrame : 0);
                return;
            }

            const segment: [number, number] = target ? [0, lastFrame] : [lastFrame, 0];
            instance.playSegments(segment, true);
            const handler = () => {
                stopAt(target ? lastFrame : 0);
                instance.removeEventListener?.('complete', handler);
                completionHandlerRef.current = null;
            };
            completionHandlerRef.current = handler;
            instance.addEventListener?.('complete', handler);
        },
        [ready]
    );

    React.useEffect(() => {
        if (!ready) return;
        if (skipNextSyncRef.current) {
            skipNextSyncRef.current = false;
            return;
        }
        animateToState(checked, prevValueRef.current === null);
    }, [checked, ready, animateToState]);

    const handleToggleClick = () => {
        if (disabled) return;
        const next = !checked;
        if (onBeforeChange && onBeforeChange(next) === false) {
            return;
        }
        if (ready) {
            animateToState(next);
            skipNextSyncRef.current = true;
        }
        onChange(next);
    };

    return (
        <button
            type="button"
            className="lottie-toggle-button"
            disabled={disabled}
            onClick={handleToggleClick}
            aria-pressed={checked}
            aria-label={checked ? 'Disable option' : 'Enable option'}
        >
            <LottiePlayer
                animationData={ToggleAnimation}
                autoplay={false}
                loop={false}
                style={{ width: '100%', height: '100%', pointerEvents: 'none' }}
                onReady={handleRef}
            />
        </button>
    );
};

function renderFieldControl(
    field: UiField,
    value: FormValue,
    onChange: (v: FormValue) => void,
    idx: number,
    controlState?: { disabled?: boolean; disabledMessage?: string; onBeforeChange?: (next: boolean) => boolean }
) {
    const name = `${buildStateKey(field.section, field.label)}_${idx}`;

    switch (field.type) {
        case 'modeSlider': {
            const selectValue = (typeof value === 'string' && value) || (typeof field.default === 'string' && field.default) || 'balanced';
            const options = [
                { value: 'fast', label: 'Fast', helper: 'Fastest analysis' },
                { value: 'balanced', label: 'Balanced', helper: 'Fast analysis, allocates more resources for hard to locate images' },
                { value: 'accurate', label: 'Accurate', helper: 'Highest accuracy (may take minutes per image)' },
            ];
            return (
                <div key={name} className="mode-slider">
                    {options.map(opt => (
                        <button
                            key={opt.value}
                            type="button"
                            className={`mode-slider__btn ${selectValue === opt.value ? 'mode-slider__btn--active' : ''}`}
                            onClick={() => onChange(opt.value)}
                            title={opt.helper}
                        >
                            <span className="mode-slider__label">{opt.label}</span>
                            <span className="mode-slider__helper">{opt.helper}</span>
                        </button>
                    ))}
                </div>
            );
        }
        case 'checkbox':
            return (
                <div key={name} className="field-control field-control--toggle">
                    <AnimatedToggle
                        disabled={controlState?.disabled}
                        checked={!!value}
                        onBeforeChange={controlState?.onBeforeChange}
                        onChange={next => onChange(next)}
                    />
                    <span className="field-control__status">{value ? 'Enabled' : 'Disabled'}</span>
                </div>
            );
        case 'radio':
            return (
                <div key={name} className="field-control field-control--options">
                    {(field.options ?? []).map(opt => (
                        <label key={opt.value} className={`field-option ${value === opt.value ? 'field-option--selected' : ''}`}>
                            <input
                                type="radio"
                                name={name}
                                value={opt.value}
                                checked={value === opt.value}
                                disabled={!!controlState?.disabled}
                                onChange={() => onChange(opt.value)}
                            />
                            <span>{opt.label}</span>
                        </label>
                    ))}
                </div>
            );
        case 'dropdown': {
            const selectValue = typeof value === 'string' ? value : '';
            return (
                <div key={name} className="field-control">
                    <select
                        className="field-select"
                        value={selectValue}
                        disabled={!!controlState?.disabled}
                        onChange={e => onChange(e.target.value)}
                    >
                        {(field.options ?? []).map(opt => (
                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                        ))}
                    </select>
                </div>
            );
        }
        case 'number': {
            const numericValue = typeof value === 'number' ? value : '';
            return (
                <div key={name} className="field-control">
                    <input
                        className="field-input"
                        type="number"
                        value={numericValue}
                        min={field.range?.min}
                        max={field.range?.max}
                        step={field.range?.step}
                        disabled={!!controlState?.disabled}
                        onChange={e => onChange(e.target.value === '' ? '' : Number(e.target.value))}
                    />
                </div>
            );
        }
        case 'date':
            return (
                <div key={name} className="field-control">
                    <input
                        className="field-input"
                        type="date"
                        value={(value as string) || ''}
                        disabled={!!controlState?.disabled}
                        onChange={e => onChange(e.target.value)}
                        placeholder={field.placeholder || ''}
                    />
                </div>
            );
        case 'folderPicker':
            return (
                <div key={name} className="field-control">
                    <input
                        className="field-input"
                        type="text"
                        value={(value as string) || ''}
                        disabled={!!controlState?.disabled}
                        onChange={e => onChange(e.target.value)}
                        placeholder="Local folder path"
                    />
                </div>
            );
        default:
            return null;
    }
}

interface SideMenuProps {
    formState: FormState;
    onFormChange: React.Dispatch<React.SetStateAction<FormState>>;
    isCollapsed: boolean;
    onToggleCollapsed: () => void;
    variant?: 'fixed' | 'drawer';
    isOpen?: boolean;
    onClose?: () => void;
    autoZoomEnabled: boolean;
    onToggleAutoZoom: (next: boolean) => void;
    directWriteSupport?: DirectWriteSupport;
    directWriteToggleBlocked?: boolean;
    onDirectWriteToggleBlocked?: () => void;
}

const SideMenu: React.FC<SideMenuProps> = ({
    formState,
    onFormChange,
    isCollapsed,
    onToggleCollapsed,
    variant = 'fixed',
    isOpen = true,
    onClose,
    autoZoomEnabled,
    onToggleAutoZoom,
    directWriteSupport,
    directWriteToggleBlocked,
    onDirectWriteToggleBlocked
}) => {

    const [expandedFields, setExpandedFields] = useState<Record<string, boolean>>(() => {
        const initial: Record<string, boolean> = {};
        typedUiSchema.forEach(field => {
            initial[buildStateKey(field.section, field.label)] = false;
        });
        return initial;
    });
    const [mapOptionsExpanded, setMapOptionsExpanded] = useState(false);

    const visibleFields = useMemo(
        () => typedUiSchema.filter(field => isStaticField(field) && isFieldVisible(field, formState)),
        [formState]
    );

    const hamburgerRef = useRef<AnimationItem | null>(null);
    const prevCollapsedRef = useRef<boolean | null>(null);
    const hamburgerCompletionRef = useRef<(() => void) | null>(null);

    const syncHamburgerAnimation = useCallback((collapsed: boolean, instant = false) => {
        if (!hamburgerRef.current) return;
        const instance = hamburgerRef.current;
        const goToTarget = () => instance.goToAndStop(collapsed ? 0 : HAMBURGER_END_FRAME, true);

        if (hamburgerCompletionRef.current) {
            instance.removeEventListener?.('complete', hamburgerCompletionRef.current);
            hamburgerCompletionRef.current = null;
        }

        if (instant || prevCollapsedRef.current === null) {
            goToTarget();
        } else if (prevCollapsedRef.current !== collapsed) {
            const segment: [number, number] = collapsed
                ? [HAMBURGER_END_FRAME, 0]
                : [0, HAMBURGER_END_FRAME];
            instance.playSegments(segment, true);
            const handler = () => {
                goToTarget();
                instance.removeEventListener?.('complete', handler);
                hamburgerCompletionRef.current = null;
            };
            hamburgerCompletionRef.current = handler;
            instance.addEventListener?.('complete', handler);
        }
        prevCollapsedRef.current = collapsed;
    }, []);

    const handleHamburgerRef = useCallback((instance: AnimationItem) => {
        hamburgerRef.current = instance;
        syncHamburgerAnimation(isCollapsed, true);
    }, [isCollapsed, syncHamburgerAnimation]);

    useEffect(() => () => {
        hamburgerRef.current = null;
        hamburgerCompletionRef.current = null;
    }, []);

    React.useEffect(() => {
        syncHamburgerAnimation(isCollapsed);
    }, [isCollapsed, syncHamburgerAnimation]);

    const prevAiRef = useRef<boolean>(!!formState[AI_TOGGLE_KEY]);
    const aiEnabled = !!formState[AI_TOGGLE_KEY];
    const contextualEnabled = !!formState[CONTEXTUAL_KEY];
    React.useEffect(() => {
        const prevAi = prevAiRef.current;
        const currentAi = aiEnabled;

        if (!prevAi && currentAi) {
            setExpandedFields(prev => ({
                ...prev,
                [CONTEXTUAL_KEY]: true
            }));
            if (!contextualEnabled) {
                onFormChange(prev => ({
                    ...prev,
                    [CONTEXTUAL_KEY]: true
                }));
            }
        } else if (prevAi && !currentAi && contextualEnabled) {
            onFormChange(prev => ({
                ...prev,
                [CONTEXTUAL_KEY]: false
            }));
        }

        prevAiRef.current = currentAi;
    }, [aiEnabled, contextualEnabled, onFormChange]);

    useEffect(() => {
        const handleEscape = (e: KeyboardEvent) => {
            if (e.key === 'Escape' && variant === 'drawer' && isOpen && onClose) {
                onClose();
            }
        };

        document.addEventListener('keydown', handleEscape);
        return () => document.removeEventListener('keydown', handleEscape);
    }, [variant, isOpen, onClose]);

    function handleFieldChange(key: string, value: FormValue) {
        if (key === DIRECT_WRITE_KEY && value === true && directWriteToggleBlocked) {
            onDirectWriteToggleBlocked?.();
            return;
        }
        onFormChange(prev => ({
            ...prev,
            [key]: value,
        }));
    }

    const toggleFieldPanel = (fieldKey: string) => {
        setExpandedFields(prev => ({
            ...prev,
            [fieldKey]: !prev[fieldKey]
        }));
    };

    const setAllFields = (expand: boolean) => {
        setExpandedFields(prev => {
            const updated = { ...prev };
            visibleFields.forEach(field => {
                const key = buildStateKey(field.section, field.label);
                updated[key] = expand;
            });
            return updated;
        });
    };

    const handleExpandAll = () => {
        setAllFields(true);
        setMapOptionsExpanded(true);
    };
    const handleCollapseAll = () => {
        setAllFields(false);
        setMapOptionsExpanded(false);
    };

    if (variant === 'drawer' && !isOpen) return null;

    const sideMenuClasses = ['side-menu', variant === 'drawer' ? 'side-menu--drawer' : '', isCollapsed ? 'side-menu--collapsed' : 'side-menu--expanded'].join(' ');
    const width = isCollapsed && variant !== 'drawer' ? SIDE_MENU_COLLAPSED_WIDTH : SIDE_MENU_EXPANDED_WIDTH;

    const renderMapCard = () => (
        <section className={`field-card ${mapOptionsExpanded ? 'field-card--expanded' : ''}`}>
            <button
                type="button"
                className="field-card__header"
                onClick={() => setMapOptionsExpanded(prev => !prev)}
                aria-expanded={mapOptionsExpanded}
            >
                <div className="field-card__title">
                    <span>Map</span>
                </div>
                <ChevronIcon expanded={mapOptionsExpanded} />
            </button>
            {mapOptionsExpanded && (
                <div className="field-card__body">
                    <div className="field-control field-control--toggle">
                        <AnimatedToggle checked={autoZoomEnabled} onChange={onToggleAutoZoom} />
                        <div className="field-control__text">
                            <span className="field-control__heading">Auto-zoom after analysis</span>
                            <p className="field-card__description">
                                Center the map to include all session markers once the batch completes.
                            </p>
                        </div>
                        <span className="field-control__status">{autoZoomEnabled ? 'Enabled' : 'Disabled'}</span>
                    </div>
                </div>
            )}
        </section>
    );

    const asideElement = (
        <aside className={sideMenuClasses} style={{ width }}>
            <div className="side-menu__header">
                {variant !== 'drawer' && (
                    <button type="button" className="hamburger-button" aria-label="Toggle configuration menu" onClick={onToggleCollapsed}>
                        <LottiePlayer
                            animationData={HamburgerAnimation}
                            autoplay={false}
                            loop={false}
                            style={{ width: '40px', height: '40px', pointerEvents: 'none' }}
                            onReady={handleHamburgerRef}
                        />
                    </button>
                )}
                <div className="side-menu__header-text">
                    <span className="side-menu__title">Configuration</span>
                    {!isCollapsed && (
                        <div className="expand-controls">
                            <button type="button" className="expand-all-button" onClick={handleExpandAll}>
                                <ChevronIcon />
                                <span>Expand all fields</span>
                            </button>
                            <button type="button" className="expand-all-button expand-all-button--collapse" onClick={handleCollapseAll}>
                                <span>Compress all fields</span>
                                <ChevronIcon expanded />
                            </button>
                        </div>
                    )}
                </div>
                {variant === 'drawer' && onClose && (
                    <button type="button" className="side-menu__close-btn" onClick={onClose} aria-label="Close drawer">×</button>
                )}
            </div>

            {!isCollapsed && (
                <form className="side-menu__form">
                    {visibleFields.map((field: UiField, idx: number) => {
                        const fieldKey = buildStateKey(field.section, field.label);
                        const isExpanded = expandedFields[fieldKey];
                        const isDirectWriteField = fieldKey === DIRECT_WRITE_KEY;
                        const controlState = isDirectWriteField && directWriteSupport
                            ? {
                                disabled: !directWriteSupport.supported,
                                disabledMessage: !directWriteSupport.supported
                                    ? DIRECT_WRITE_DISABLED_MESSAGE
                                    : undefined,
                                onBeforeChange: (next: boolean) => {
                                    if (next && directWriteToggleBlocked) {
                                        onDirectWriteToggleBlocked?.();
                                        return false;
                                    }
                                    return true;
                                },
                            }
                            : undefined;

                        return (
                            <React.Fragment key={fieldKey}>
                                <section className={`field-card ${isExpanded ? 'field-card--expanded' : ''}`}>
                                    <button
                                        type="button"
                                        className="field-card__header"
                                        onClick={() => toggleFieldPanel(fieldKey)}
                                        aria-expanded={isExpanded}
                                    >
                                        <div className="field-card__title">
                                            <span>{field.label}</span>
                                            {field.required && <span className="required-indicator">*</span>}
                                        </div>
                                        <ChevronIcon expanded={isExpanded} />
                                    </button>
                                    {isExpanded && (
                                        <div className="field-card__body">
                                            {field.tooltip && (!isDirectWriteField || (directWriteSupport && directWriteSupport.supported)) && (
                                                <p className="field-card__tooltip">
                                                    {field.tooltip}
                                                </p>
                                            )}
                                            {renderFieldControl(
                                                field,
                                                formState[fieldKey],
                                                value => handleFieldChange(fieldKey, value),
                                                idx,
                                                controlState
                                            )}
                                            {controlState?.disabled && controlState.disabledMessage && (
                                                <p className="field-card__tooltip field-card__tooltip--warning">
                                                    {controlState.disabledMessage}
                                                </p>
                                            )}
                                            {field.description && !isDirectWriteField && (
                                                <p className="field-card__description">{field.description}</p>
                                            )}
                                            {isDirectWriteField && !directWriteSupport?.supported && !controlState?.disabledMessage && (
                                                <p className="field-card__description">{DIRECT_WRITE_DISABLED_MESSAGE}</p>
                                            )}
                                        </div>
                                    )}
                                </section>
                                {fieldKey === RATE_CONNECTION_KEY && renderMapCard()}
                            </React.Fragment>
                        );
                    })}
                </form>
            )}
        </aside>
    );

    if (variant === 'drawer') {
        return (
            <div className="side-menu__overlay" onClick={onClose}>
                <div onClick={(e) => e.stopPropagation()}>
                    {asideElement}
                </div>
            </div>
        );
    }

    return asideElement;
};

export default SideMenu;
